import { brandPalette } from "../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  Grid,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { RiEditBoxLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { openCreateGroup } from "../../../modules/admin-modules/models/Actions";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
  surface: brandPalette.cream,
  canvas: brandPalette.warmBeige,
  muted: "rgba(69, 11, 17, 0.65)",
};

const typeLabel = (type) => {
  if (type === 0 || type === "0") return "Online";
  if (type === 1 || type === "1") return "Center";
  return "—";
};

const GroupsTable = ({ setAdd, setSelectedData, searchTerm = "" }) => {
  const groups = useSelector((state) => state.groups.groups);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.groups.isLoading);

  const filteredGroups = useMemo(() => {
    const list = Array.isArray(groups) ? groups : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter((g) => g?.name?.toLowerCase().includes(q));
  }, [groups, searchTerm]);

  if (isLoading) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} height="140px" borderRadius="16px" startColor="gray.200" endColor="gray.300" />
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (!filteredGroups.length) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <Box
          border="1px dashed"
          borderColor={C.border}
          borderRadius="16px"
          bg="var(--surface-canvas)"
          py={16}
          textAlign="center"
        >
          <Text fontWeight="800" color={C.primary} fontSize="lg">
            {searchTerm ? "No groups match your search" : "No groups yet"}
          </Text>
          <Text mt={2} color={C.muted} fontSize="sm" maxW="md" mx="auto">
            Create a group to assign students to a grade and delivery type (online or center).
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
        {filteredGroups.map((group) => (
          <VStack
            key={group.id}
            align="stretch"
            spacing={4}
            border="1px solid"
            borderColor={C.border}
            borderRadius="16px"
            p={5}
            bg="var(--surface-raised)"
            boxShadow="0 4px 18px rgba(69, 11, 17, 0.06)"
            transition="box-shadow 0.2s, border-color 0.2s"
            _hover={{
              boxShadow: "0 10px 28px rgba(69, 11, 17, 0.1)",
              borderColor: "rgba(91, 13, 22, 0.35)",
            }}
          >
            <Flex justify="space-between" align="flex-start" gap={3}>
              <Box minW={0}>
                <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                  Group name
                </Text>
                <Text fontSize="lg" fontWeight="800" color={C.secondary} mt={1} noOfLines={2}>
                  {group?.name}
                </Text>
              </Box>
              <Button
                size="sm"
                leftIcon={<RiEditBoxLine />}
                variant="outline"
                borderColor={C.border}
                color={C.primary}
                borderRadius="full"
                fontWeight="700"
                flexShrink={0}
                _hover={{ bg: C.canvas, borderColor: C.primary }}
                onClick={() => {
                  setSelectedData({ ...group, subject_id: group?.subject?.id });
                  setAdd(false);
                  dispatch(openCreateGroup());
                }}
              >
                Edit
              </Button>
            </Flex>

            <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
            <Box pt={1} borderTop="1px solid" borderColor="var(--border-subtle)">
                <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                  Subject
                </Text>
                <Text fontSize="sm" fontWeight="700" color={C.secondary} mt={1} noOfLines={1}>
                  {group.subject.name}
                </Text>
              </Box>
              <Box textAlign={{ base: "left", sm: "right" }}>
                <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                  Type
                </Text>
                <Text fontSize="sm" fontWeight="700" color={C.primary} mt={1}>
                  {typeLabel(group?.type)}
                </Text>
              </Box>
            </Flex>
          </VStack>
        ))}
      </Grid>
    </Box>
  );
};

export default GroupsTable;
